import React, { useState, useEffect, useRef } from "react";
import { Sparkles, Send, BookOpen, Trash2 } from "lucide-react";
import { api, API, getToken } from "../lib/api";
import { Markdown } from "./Markdown";
import { toast } from "sonner";

const card = { background: "var(--teal-tint)", borderColor: "var(--teal)" };

export const LearningCompanion = ({ courseId, encounterId, user, preview }) => {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!user || preview) return;
    let ignore = false;
    (async () => {
      try {
        const [{ data: s }, { data: h }] = await Promise.all([
          api.get(`/me/companion/${courseId}/status`), api.get(`/me/companion/${courseId}/history`)]);
        if (!ignore) { setStatus(s); setMessages(h || []); }
      } catch { if (!ignore) setStatus({ available: false, message: "The Learning Companion is not available right now." }); }
    })();
    return () => { ignore = true; };
  }, [courseId, user, preview]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages.length, busy]);

  const ask = async () => {
    const question = q.trim();
    if (!question || busy) return;
    setQ(""); setBusy(true);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: question }, { id: "streaming", role: "assistant", content: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/me/companion/${courseId}/ask`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ question, encounter_id: encounterId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "The Companion could not respond just now.");
      }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const ev = /event: (\w+)/.exec(p)?.[1]; const dataLine = p.split("\n").find((l) => l.startsWith("data: "));
          if (!ev || !dataLine) continue;
          const data = JSON.parse(dataLine.slice(6));
          if (ev === "token") setMessages((m) => m.map((x) => x.id === "streaming" ? { ...x, content: x.content + data } : x));
          if (ev === "done") { const final = JSON.parse(data); setMessages((m) => m.map((x) => x.id === "streaming" ? final : x)); }
        }
      }
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== "streaming"));
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear your private Companion conversation for this course?")) return;
    await api.delete(`/me/companion/${courseId}/history`); setMessages([]);
  };

  return (
    <div className="mt-10 rounded-2xl border p-6 sm:p-8" style={card} data-testid="learning-companion">
      <div className="flex items-start gap-3 mb-4">
        <Sparkles className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: "var(--teal)" }} />
        <div className="flex-1">
          <h3 className="font-serif text-xl" style={{ color: "var(--teal-deep)" }}>Explore with the Learning Companion</h3>
          <p className="text-sm mt-1 leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Ask about the approved materials for this course. The Companion will respond from the sources provided for this journey and show you where its response comes from.
          </p>
        </div>
        {messages.length > 0 && <button onClick={clear} data-testid="companion-clear-btn" className="p-1.5" title="Clear my conversation"><Trash2 className="w-4 h-4" style={{ color: "var(--text-muted)" }} /></button>}
      </div>

      {!user || preview ? (
        <p className="text-sm italic" style={{ color: "var(--text-muted)" }} data-testid="companion-signin-note">
          {preview ? "The Companion is available to enrolled members outside preview." : "Sign in and enroll to explore with the Companion."}
        </p>
      ) : status && !status.available ? (
        <p className="text-sm italic" style={{ color: "var(--text-muted)" }} data-testid="companion-unavailable">{status.message}</p>
      ) : (
        <>
          {status?.sources?.length > 0 && (
            <div className="text-[11px] mb-4 flex flex-wrap items-center gap-1.5" style={{ color: "var(--teal-deep)" }} data-testid="companion-sources">
              <BookOpen className="w-3.5 h-3.5" /> Sources for this course: {status.sources.join(" · ")}
            </div>
          )}
          <div className="space-y-4 max-h-[28rem] overflow-y-auto pr-1" data-testid="companion-messages">
            {messages.length === 0 && <p className="text-sm italic" style={{ color: "var(--text-muted)" }}>Your conversation here is private to you and this course.</p>}
            {messages.map((m) => (
              <div key={m.id} data-testid={`companion-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className="rounded-xl px-4 py-3 text-sm max-w-[92%]" style={m.role === "user"
                  ? { background: "var(--bg-cream)", color: "var(--text-obsidian)", border: "1px solid var(--border-cream)" }
                  : { background: "transparent" }}>
                  {m.role === "user" ? m.content : (
                    <>
                      <Markdown className="text-sm" style={{ color: "var(--text-obsidian)" }}>{m.content || (m.streaming ? "…" : "")}</Markdown>
                      {m.citations?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="companion-citations">
                          {m.citations.map((c) => <span key={c.label} className="rounded-full px-2 py-0.5 text-[10px] font-mono" style={{ background: "var(--bg-cream)", color: "var(--teal-deep)", border: "1px solid var(--teal)" }}>{c.label}</span>)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="mt-4 flex gap-2">
            <textarea data-testid="companion-question-input" rows={2} value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="Ask about the sources for this course…" disabled={busy}
              className="flex-1 rounded-lg p-3 text-sm border resize-none focus:outline-none disabled:opacity-60"
              style={{ background: "var(--bg-cream)", borderColor: "var(--border-cream)" }} />
            <button data-testid="companion-ask-btn" onClick={ask} disabled={busy || !q.trim()}
              className="self-end inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium disabled:opacity-50"
              style={{ background: "var(--teal)", color: "var(--bg-cream)" }}>
              <Send className="w-4 h-4" /> {busy ? "Listening…" : "Ask"}
            </button>
          </div>
          <p className="text-[11px] mt-3 italic" style={{ color: "var(--text-muted)" }}>The Companion examines sources with you; it is not a spiritual authority. Then don't borrow my eyes. Use yours.</p>
        </>
      )}
    </div>
  );
};
